import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePatientDetails } from '../../hooks/usePatientDetails';
import { useRoomProperties } from '../../hooks/useRoomProperties';
import { usePreferredStaffLoader } from '../../hooks/usePreferredStaffOptions';
import { useDrawerEditSlide } from '../../hooks/useDrawerEditSlide';
import { useHasBeenTrue } from '../../hooks/useHasBeenTrue';
import { PatientDetailsCard } from '../PatientDetails/PatientDetailsCard';
import { DrawerShell } from '../UI/DrawerShell';
import { PatientDrawerHeader } from './PatientDrawerHeader';
import { PatientDetailsViewBody } from './PatientDetailsViewBody';
import styles from './PatientDetailsDrawer.module.css';

interface PatientDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string | null;
  locationId: string | null;
  mode: 'view' | 'create';
  onPatientUpdated?: () => void;
}

export const PatientDetailsDrawer = ({
  isOpen,
  onClose,
  patientId,
  locationId,
  mode,
  onPatientUpdated
}: PatientDetailsDrawerProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [refreshToken, setRefreshToken] = useState(0);

  const { isEditing, isEditSliding, startEdit, endEdit, close } = useDrawerEditSlide(
    isOpen,
    onClose
  );

  // `details` is null until this patient's own fetch lands — including right
  // after a switch to another patient — so it doubles as the load gate.
  // `isRefreshing` is the narrower case: a refresh of the patient already on
  // screen, where only the values in the view slot are known to be stale.
  const {
    data: details,
    isRefreshing,
    error: hasDetailsError
  } = usePatientDetails(locationId, patientId, refreshToken);

  // Only the *edit* form is gated on this: it resolves the patient's room needs
  // against the table, and an incomplete lookup silently drops them so a save
  // wipes them. Everything else here — the read-only card, treatments, audit
  // log, the create form — either cannot save room needs or has none to lose,
  // and room needs are optional, so none of it waits on this table.
  const {
    isLoaded: isRoomPropertiesLoaded,
    hasError: hasRoomPropertiesError,
    retry: retryRoomProperties
  } = useRoomProperties(isOpen);

  // Latched: once the table has arrived, a later `clearRoomProperties` must not
  // unmount an edit in progress and throw away what the user typed.
  const canEdit = useHasBeenTrue(isRoomPropertiesLoaded);

  usePreferredStaffLoader(locationId, isOpen);

  const handlePatientUpdated = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
    onPatientUpdated?.();
  }, [onPatientUpdated]);

  // The drawer is never unmounted by PatientsList — `isOpen` only toggles CSS —
  // so re-clicking the same patient changes no effect dependency. Bumping the
  // token is the only way back from a failed fetch.
  const handleRetryDetails = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  const isCreateMode = mode === 'create';
  const title = isCreateMode ? t('add-patient') : (details?.profile.name ?? '');
  // Back leaves the edit view, so it only belongs on a header that has one open.
  const onBack = !isCreateMode && isEditing && details ? endEdit : undefined;

  // Both branches feed the same shell, and neither renders until the drawer is
  // open and a location is known — so a closed drawer holds no content at all,
  // and with no header, no close button either.
  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={close}
      ariaLabel={t('details')}
      titleId={titleId}
      header={
        isOpen && locationId ? (
          <PatientDrawerHeader titleId={titleId} title={title} onBack={onBack} />
        ) : null
      }
    >
      {isOpen && locationId && isCreateMode && (
        // Ungated: a new patient has no room needs to lose, and the field is
        // optional, so a missing table must not block creation.
        <div className={styles.content}>
          <PatientDetailsCard
            locationId={locationId}
            mode="create"
            onEditStart={startEdit}
            onEditEnd={close}
            onPatientUpdated={onPatientUpdated}
          />
        </div>
      )}
      {isOpen && locationId && !isCreateMode && (
        <PatientDetailsViewBody
          locationId={locationId}
          details={details}
          isRefreshing={isRefreshing}
          hasDetailsError={hasDetailsError}
          isEditing={isEditing}
          isEditSliding={isEditSliding}
          canEdit={canEdit}
          hasRoomPropertiesError={hasRoomPropertiesError}
          onRetryDetails={handleRetryDetails}
          onRetryRoomProperties={retryRoomProperties}
          onEditStart={startEdit}
          onEditEnd={endEdit}
          onPatientUpdated={handlePatientUpdated}
        />
      )}
    </DrawerShell>
  );
};
